import { useContext, useState, useEffect } from "react";
import "./login.scss";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../firebase";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../store/AuthContext"
import { collection, getDocs, where, query } from "firebase/firestore";
import { uploadErrorLogToFirestore } from "../../shared/utils/logUtils";
import { registrarAcceso } from "../../shared/utils/accesosUtils";
import { usePerfTrace } from "../../shared/hooks";
import logoUrl from "../../assets/images/logoScreen.png"

/**
 * Componente de la página de inicio de sesión.
 * 
 * @returns {JSX.Element} Elemento JSX que representa la página de inicio de sesión.
 */
const Login = () => {
  usePerfTrace("login");
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isSending, setIsSending] = useState(false);


  const { dispatch } = useContext(AuthContext)

  // Si AuthContext cerró la sesión por un desajuste entre la sesión viva de
  // Firebase Auth y la identidad mostrada por la app, informamos del motivo.
  useEffect(() => {
    const aviso = sessionStorage.getItem("authMismatch");
    if (aviso) {
      setError(aviso);
      sessionStorage.removeItem("authMismatch");
    }
  }, []);

  /**
 * Maneja el evento de inicio de sesión.
 * 
 * @param {Event} e Evento del formulario de inicio de sesión.
 */
  const handleLogin = (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    setError("");
    setIsSubmitting(true);

    signInWithEmailAndPassword(auth, email, password)
      .then(async (userCredential) => {
        const user = userCredential.user;

        const q = query(collection(db, 'servicios'), where('admins', 'array-contains', email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          setError("Tu cuenta no tiene acceso a ningún servicio. Contacta con el administrador.");
          setIsSubmitting(false);
          return;
        }

        // Registro del acceso (best-effort, con try/catch interno: un fallo no
        // rompe el login). Se guarda en el primer servicio del que el usuario es
        // admin (el mismo que usará AuthContext como currentService).
        // IMPORTANTE: hay que esperarlo — justo después se hace una recarga
        // completa (window.location.href) que mataría la escritura en vuelo.
        await registrarAcceso({ servicioId: querySnapshot.docs[0].id, user, origen: 'dashboard' });

        dispatch({ type: "LOGIN", payload: user });
        window.location.href = '/';
      })
      .catch((err) => {
        const code = err?.code || "";
        let message;
        switch (code) {
          case "auth/invalid-credential":
          case "auth/invalid-login-credentials":
          case "auth/wrong-password":
          case "auth/user-not-found":
            message = "Email o contraseña incorrectos.";
            break;
          case "auth/invalid-email":
            message = "El formato del correo electrónico no es válido.";
            break;
          case "auth/too-many-requests":
            message = "Demasiados intentos fallidos. Espera unos minutos antes de volver a intentarlo.";
            break;
          case "auth/network-request-failed":
            message = "Sin conexión. Comprueba tu red e inténtalo de nuevo.";
            break;
          case "auth/user-disabled":
            message = "Esta cuenta está deshabilitada. Contacta con el administrador.";
            break;
          default:
            message = "No se pudo iniciar sesión. Inténtalo de nuevo más tarde.";
        }
        setError(message);
        setIsSubmitting(false);
        uploadErrorLogToFirestore(`Login error (${code}): ${err?.message || ""}`);
      });
  };

  /**
 * Maneja el proceso de restablecimiento de contraseña.
 * Si el usuario ha introducido un correo electrónico, envía un correo
 * con instrucciones para restablecer la contraseña.
 * Si ocurre un error durante el envío, se notifica al usuario.
 *
 * @function
 */
  const handleResetPassword = async (e) => {
    e?.preventDefault?.();
    if (!email?.trim()) {
      alert("Por favor, introduce tu correo electrónico.");
      return;
    }
    if (isSending) return;
    setIsSending(true);
    setError(false);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      alert("Se ha enviado un correo para restablecer tu contraseña. Revisa tu bandeja de entrada y la carpeta de spam.");
      setIsResettingPassword(false);
    } catch (err) {
      console.error("Error al enviar correo de recuperación:", err);
      const code = err?.code || "";
      if (code === "auth/user-not-found") {
        alert("No existe ninguna cuenta con ese correo. Comprueba el email o regístrate.");
      } else if (code === "auth/invalid-email") {
        alert("El correo electrónico no es válido.");
      } else if (code === "auth/too-many-requests") {
        alert("Demasiados intentos. Espera un momento y vuelve a intentarlo.");
      } else {
        alert("No se pudo enviar el correo. Revisa que el email sea correcto o inténtalo más tarde.");
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleForgotPasswordClick = () => {
    // Cambia el estado para mostrar el formulario de recuperación de contraseña
    setIsResettingPassword(true);
  };

  const handleBackToLogin = () => {
    // Cambia el estado para volver al formulario de inicio de sesión
    setIsResettingPassword(false);
  };

  return (
    <div className="login">
      <div className="branding">
        <img src={logoUrl} alt="Logo" className="branding-logo" />
        <span className="branding-title">S E N T R Y</span>
      </div>
      <div className="loginContainer">
        <h2 className="loginTitle">{isResettingPassword ? "Cambiar contraseña" : "Inicia sesión"}</h2>

        <form onSubmit={isResettingPassword ? handleResetPassword : handleLogin} className="loginForm">
          <input
            type="email"
            placeholder="Correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="loginInput"
            data-testid="email-input"
            disabled={isSending || isSubmitting}
            autoComplete="username"
          />
          {!isResettingPassword && (
            <input
              type="password"
              placeholder="Contraseña"
              onChange={(e) => setPassword(e.target.value)}
              className="loginInput"
              data-testid="password-input"
              disabled={isSubmitting}
              autoComplete="current-password"
            />
          )}

          {error && !isResettingPassword && (
            <div className="loginError" role="alert" data-testid="login-error">
              {error}
            </div>
          )}

          <button type="submit" className="loginButton" disabled={isSending || isSubmitting}>
            {isResettingPassword
              ? (isSending ? "Enviando…" : "Recuperar")
              : (isSubmitting ? "Iniciando…" : "Iniciar Sesión")}
          </button>
        </form>

        {isResettingPassword ? (
          <p className="forgotPassword" onClick={handleBackToLogin}>
            Volver a la pantalla de inicio de sesión
          </p>
        ) : (
          <p className="forgotPassword" onClick={handleForgotPasswordClick}>
            ¿Has olvidado tu contraseña?
          </p>
        )}
      </div>

    </div>
  );
};

export default Login;